import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { TranslateFn } from "../../i18n";
import type { SizingResult, SizeGuide } from "../types";
import { TryOnGenerationBadge } from "./TryOnGenerationBadge";
import garmentJacketImg from "../assets/icon-garment-jacket.png";
import garmentPantsImg from "../assets/icon-garment-pants.png";
import garmentVestImg from "../assets/icon-garment-vest.png";

// Same helper as desktop SizeResultView — picks the built-in line-art PNG
// for common section names so tuxedo Jacket / Pants / Vest match desktop.
function garmentIconForSection(name: string): string | null {
  const n = name.toLowerCase();
  if (n.includes("jacket") || n.includes("blazer") || n.includes("coat")) return garmentJacketImg as unknown as string;
  if (n.includes("pant") || n.includes("trouser") || n.includes("suit pants")) return garmentPantsImg as unknown as string;
  if (n.includes("vest") || n.includes("waistcoat") || n.includes("gilet")) return garmentVestImg as unknown as string;
  return null;
}

/**
 * Mobile-only multi-section size results — replaces the desktop section
 * picker on phones. Matches the reference design:
 *
 *   Sizing Profile           ← eyebrow header
 *
 *   ┌─ product / try-on photo ─┐
 *   │                           │
 *   └───────────────────────────┘
 *      Midnight Satin Tuxedo
 *
 *   STRUCTURED JACKET
 *           50
 *   ─────────────────
 *   TAILORED PANT
 *           XL
 *
 * Bottom CTA changes based on try-on state:
 *   - No try-on yet → [ 📷  See how it looks on yourself ]
 *   - Try-on done   → [ Try Again ]  [ Done ]
 */

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function BookmarkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function FitLinesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <circle cx="7" cy="9" r="2" />
      <circle cx="17" cy="15" r="2" />
    </svg>
  );
}

function FullSizeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}

export interface MultiSectionMobileProps {
  productImage: string;
  productTitle: string;
  sizingResult: SizingResult;
  sizeGuide: SizeGuide | null;
  sectionEntries: Array<{ name: string; secResult: { recommendedSize: string } }>;
  /** Per-section overrides (user's picks). When a section has an override,
   *  that card shows the user's label + "YOUR SELECTION" state instead of
   *  the backend recommendation. */
  pendingCustomSizes?: Record<string, {
    sectionName: string;
    selectedSize: string;
    selectedLength?: string;
    displayLabel: string;
  }>;
  onSelectSection: (name: string) => void;
  onTryOn: () => void;
  tryOnProcessing?: boolean;
  /** Epoch ms timestamp the try-on request started — drives the
   *  TryOnGenerationBadge's progress + ETA copy. */
  tryOnStartedAt?: number | null;
  /** When set, the image area shows the try-on result instead of productImage. */
  resultImageUrl?: string | null;
  /** Try-on has finished and result is ready. Switches the bottom CTA into
   *  "Try Again" + "Done" mode. */
  tryOnDone?: boolean;
  /** Reset try-on state and reopen the photo guide for another attempt. */
  onTryAgain?: () => void;
  /** Close the SDK / continue shopping. */
  onClose?: () => void;
  /** Optional bottom CTA group for history/current product commerce actions. */
  resultActionNode?: ReactNode;
  /** Optional demo/catalog recommendation panel rendered under the size cards. */
  outfitRecommendationNode?: ReactNode;
  /** Optional measurement-line overlay to render on top of the result image
   *  (mediapipe toggle). Only displayed when `showLines` is true. */
  overlayNode?: ReactNode;
  /** Whether the mediapipe lines toggle is currently on. */
  showLines?: boolean;
  /** Toggle the mediapipe lines overlay. */
  onToggleLines?: () => void;
  /** Open the generated try-on image in the full-size preview. */
  onOpenImage?: () => void;
  /** Download the generated try-on image. */
  onDownload?: () => void;
  /** Action row rendered above the result image (show fit/full size/download). */
  imageActionBar?: ReactNode;
  /** Rating widget rendered over the lower-left of the result image. */
  feedbackSlot?: ReactNode;
  /** Bubble image natural dimensions up to the parent (for the overlay sizing). */
  onImageLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  profileCompletionCta?: ReactNode;
  t: TranslateFn;
}

export function MultiSectionMobile({
  productImage,
  productTitle,
  sizingResult,
  sizeGuide,
  sectionEntries,
  pendingCustomSizes,
  onSelectSection,
  onTryOn,
  tryOnProcessing,
  tryOnStartedAt,
  resultImageUrl,
  tryOnDone,
  onTryAgain,
  onClose,
  resultActionNode,
  outfitRecommendationNode,
  overlayNode,
  showLines,
  onToggleLines,
  onOpenImage,
  onDownload,
  imageActionBar,
  feedbackSlot,
  onImageLoad,
  profileCompletionCta,
  t,
}: MultiSectionMobileProps) {
  // Pick the image to display: try-on result if available, else product photo.
  const heroImage = resultImageUrl || productImage;
  const showingTryOn = !!resultImageUrl;
  const resultImageRef = useRef<HTMLImageElement | null>(null);
  const [loadedResultImageUrl, setLoadedResultImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!resultImageUrl) setLoadedResultImageUrl(null);
  }, [resultImageUrl, tryOnProcessing]);

  useLayoutEffect(() => {
    if (!resultImageUrl || tryOnProcessing) return;
    const img = resultImageRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoadedResultImageUrl(resultImageUrl);
  }, [resultImageUrl, tryOnProcessing]);

  const resultImageLoading = showingTryOn && !tryOnProcessing && loadedResultImageUrl !== resultImageUrl;
  const resultImageInteractive = showingTryOn && !tryOnProcessing && loadedResultImageUrl === resultImageUrl;
  const resultImageAvailable = showingTryOn && !tryOnProcessing;

  return (
    <div className="ps-msr-root">
      {/* ── Scrolling content area ── */}
      <div className="ps-msr-scroll">
        {/* Eyebrow */}
        <div className="ps-msr-eyebrow">{t("Sizing Profile")}</div>

        {/* Product / try-on hero — bigger when showing the try-on result */}
        <div className="ps-msr-product">
          {resultImageAvailable && imageActionBar}
          <div className={`ps-msr-product-img-wrap${showingTryOn ? " ps-tryon" : ""}`}>
            <img
              ref={resultImageRef}
              key={heroImage}
              src={heroImage}
              alt={productTitle}
              className={`ps-msr-product-img${resultImageLoading ? " ps-result-image-pending" : ""}`}
              onLoad={(event) => {
                if (showingTryOn && !tryOnProcessing) setLoadedResultImageUrl(heroImage);
                onImageLoad?.(event);
              }}
              onClick={resultImageInteractive && onOpenImage ? onOpenImage : undefined}
              style={resultImageInteractive && onOpenImage ? { cursor: "zoom-in" } : undefined}
            />
            {resultImageLoading && (
              <div className="ps-result-image-loading" role="status" aria-live="polite">
                <span className="ps-result-image-loading-spinner" aria-hidden="true" />
                <span>{t("Loading try-on result")}</span>
              </div>
            )}
            {/* Mediapipe overlay (parent renders <svg>) only when toggle is on */}
            {showLines && resultImageAvailable && overlayNode}
            {/* Try-on processing pill — same component as desktop, anchored
                to the bottom of the image hero on mobile. */}
            {tryOnProcessing && tryOnStartedAt != null && (
              <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt} t={t} />
            )}
            {resultImageAvailable && feedbackSlot}
          </div>
          <h2 className="ps-msr-product-name">{productTitle}</h2>
        </div>

        {/* Section cards — tall rows with big centered size value */}
        <div className="ps-msr-sections">
          {sectionEntries.map(({ name, secResult }) => {
            const cleanName = name.replace(/\s*[—–-]\s*.*/g, "");
            const sec = secResult as { recommendedSize?: string; size?: string; found?: boolean };
            const override = pendingCustomSizes?.[name];
            const isOverridden = !!override;
            const sizeValue = sec.found === false
              ? t("No fit")
              : (isOverridden ? override.displayLabel : (sec.size || secResult.recommendedSize));
            const builtInIcon = garmentIconForSection(name);
            const iconSrc = builtInIcon || null;
            return (
              <button
                key={name}
                type="button"
                className={`ps-msr-section-card${isOverridden ? " ps-overridden" : ""}`}
                onClick={() => onSelectSection(name)}
              >
                {iconSrc && (
                  <img src={iconSrc} alt="" className="ps-msr-section-icon-lg" />
                )}
                <span className="ps-msr-section-name">{cleanName.toUpperCase()}</span>
                <span className="ps-msr-section-size">{sizeValue}</span>
                <span
                  className="ps-msr-section-rec"
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    marginTop: "4px",
                    color: isOverridden ? "#b45309" : "var(--ps-text-secondary, #6b7280)",
                  }}
                >
                  {isOverridden ? t("your selection · not suggested") : t("suggested")}
                </span>
              </button>
            );
          })}
        </div>
        {outfitRecommendationNode}
        {profileCompletionCta}
      </div>

      {/* ── Fixed bottom action bar (always visible) ── */}
      <div className="ps-msr-bottom">
        {resultActionNode ? (
          <div className="ps-msr-bottom-row ps-msr-commerce-row">
            {resultActionNode}
          </div>
        ) : tryOnDone ? (
          <div className="ps-msr-bottom-row">
            <button
              type="button"
              className="ps-msr-tryon-cta ps-secondary"
              onClick={onTryAgain}
            >
              {t("Try Again")}
            </button>
            <button
              type="button"
              className="ps-msr-tryon-cta"
              onClick={onClose}
            >
              {t("Done")}
            </button>
          </div>
        ) : (
          // Try-On stays enabled even when the backend couldn't find a size —
          // it's a visual preview, independent of the recommendation.
          <button
            type="button"
            className="ps-msr-tryon-cta"
            onClick={onTryOn}
            disabled={tryOnProcessing}
          >
            <CameraIcon />
            {tryOnProcessing ? t("Processing...") : t("See how it looks on yourself")}
          </button>
        )}
      </div>

      {/* unused — keeps the prop typed for future use */}
      {sizeGuide ? null : null}
    </div>
  );
}
